import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './erpnext.css';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import QuickEntry from './QuickEntry';
import KeyboardShortcutsModal from './KeyboardShortcuts';

// ERPNextShell: the new top-level layout. Drop any page inside this and it
// adopts the ERPNext look. Use <PageHeader> at the top of each page.
//
// Persistence: sidebar collapsed state stored in localStorage so it survives
// reloads (matches ERPNext behaviour).
export default function ERPNextShell({ children, user, setUser }) {
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('erp_sidebar_collapsed') === '1');
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [quickEntry, setQuickEntry] = useState({ open: false, doctype: null });
  const location = useLocation();

  useEffect(() => {
    localStorage.setItem('erp_sidebar_collapsed', collapsed ? '1' : '0');
  }, [collapsed]);

  // Handle keyboard shortcut: \ toggles sidebar; Ctrl+N opens quick entry on
  // any doctype list page.
  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target?.tagName || '').toLowerCase();
      const inField = tag === 'input' || tag === 'textarea' || e.target?.isContentEditable;
      if (!inField && e.key === '\\') { e.preventDefault(); setCollapsed(c => !c); }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'n' && !inField) {
        const m = location.pathname.match(/^\/doctype\/([^/]+)/);
        if (m) {
          e.preventDefault();
          setQuickEntry({ open: true, doctype: m[1] });
        }
      }
      if (e.key === 'Escape' && quickEntry.open) setQuickEntry({ open: false, doctype: null });
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [location.pathname, quickEntry.open]);

  // Listen for "open-quick-entry" custom event from any descendant.
  useEffect(() => {
    const handler = (e) => setQuickEntry({ open: true, doctype: e.detail?.doctype });
    window.addEventListener('erp:quick-entry', handler);
    return () => window.removeEventListener('erp:quick-entry', handler);
  }, []);

  return (
    <div className={`erp-shell${collapsed ? ' collapsed' : ''}`}>
      <div className={`erp-sidebar-wrap${mobileSidebarOpen ? ' open' : ''}`}>
        <Sidebar collapsed={collapsed} />
      </div>
      <Topbar
        user={user}
        setUser={setUser}
        onToggleSidebar={() => {
          if (window.innerWidth <= 768) setMobileSidebarOpen(o => !o);
          else setCollapsed(c => !c);
        }}
      />
      <main className="erp-content" tabIndex={-1}>
        {children}
      </main>
      <QuickEntry
        open={quickEntry.open}
        doctype={quickEntry.doctype}
        onClose={() => setQuickEntry({ open: false, doctype: null })}
        onCreated={() => setQuickEntry({ open: false, doctype: null })}
      />
      <KeyboardShortcutsModal />
    </div>
  );
}
